import React, { useState } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Home, Building2, Users, LogOut, Menu, X, Shield, MessageSquare, Headphones, ChevronLeft, MapPin, Sparkles, TrendingUp, UserPlus, Activity } from 'lucide-react';

const SECTIONS = [
  {
    title: 'Försäljning',
    items: [
      { path: '/superadmin/sales', label: 'Ny kund', icon: Sparkles },
      { path: '/superadmin/sales-stats', label: 'Statistik', icon: TrendingUp },
      { path: '/superadmin/sales-team', label: 'Säljare', icon: UserPlus },
    ],
  },
  {
    title: 'Plattform',
    items: [
      { path: '/superadmin', label: 'Översikt', icon: Home },
      { path: '/superadmin/companies', label: 'Företag', icon: Building2 },
      { path: '/superadmin/users', label: 'Användare', icon: Users },
      { path: '/superadmin/health', label: 'Systemhälsa', icon: Activity },
      { path: '/superadmin/activity-map', label: 'Live Karta', icon: MapPin },
    ],
  },
  {
    title: 'Support',
    items: [
      { path: '/superadmin/live-chat', label: 'Live Chatt', icon: MessageSquare },
      { path: '/superadmin/public-chats', label: 'Publika chattar', icon: MessageSquare },
      { path: '/superadmin/support-agents', label: 'Kundtjänst', icon: Headphones },
    ],
  },
];

const allItems = SECTIONS.flatMap(s => s.items);

const SuperAdminLayout = () => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(false);

  const handleLogout = () => { logout(); navigate('/login'); };

  const isActive = (path) => {
    if (path === '/superadmin') return location.pathname === '/superadmin';
    return location.pathname.startsWith(path);
  };

  const currentPage = allItems.find(
    item => item.path === location.pathname || (item.path !== '/superadmin' && location.pathname.startsWith(item.path))
  ) || allItems[0];

  return (
    <div className="flex h-screen bg-background">
      {sidebarOpen && <div className="fixed inset-0 bg-black/30 z-40 lg:hidden" onClick={() => setSidebarOpen(false)} />}

      <aside className={`fixed lg:static inset-y-0 left-0 z-50 bg-[#1a1a1a] text-white flex flex-col transition-all duration-300 ${
        sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
      } ${collapsed ? 'w-[72px]' : 'w-60'}`}>

        <div className="flex items-center justify-between px-4 h-14 border-b border-white/10 flex-shrink-0">
          {!collapsed && (
            <div className="flex items-center gap-2">
              <Shield size={18} className="text-[#E8613A]" />
              <span className="text-base font-bold tracking-tight" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
                Operio Admin
              </span>
            </div>
          )}
          {collapsed && <Shield size={18} className="text-[#E8613A] mx-auto" />}
          <button onClick={() => setSidebarOpen(false)} className="lg:hidden text-white/60 hover:text-white" data-testid="sa-close-sidebar">
            <X size={18} />
          </button>
          <button onClick={() => setCollapsed(!collapsed)} className="hidden lg:flex p-1 text-white/40 hover:text-white transition-transform">
            <ChevronLeft size={16} className={`transition-transform duration-300 ${collapsed ? 'rotate-180' : ''}`} />
          </button>
        </div>

        <nav className="flex-1 overflow-y-auto py-3 px-2" data-testid="sa-sidebar-nav">
          {SECTIONS.map((section, si) => (
            <div key={section.title} className={si > 0 ? 'mt-4' : ''}>
              {!collapsed && (
                <p className="px-3 mb-1 text-[10px] font-semibold uppercase tracking-[0.12em] text-white/30 select-none">
                  {section.title}
                </p>
              )}
              {collapsed && si > 0 && <div className="mx-3 mb-2 border-t border-white/10" />}
              <div className="space-y-0.5">
                {section.items.map((item) => {
                  const Icon = item.icon;
                  const active = isActive(item.path);
                  return (
                    <Link key={item.path} to={item.path}
                      data-testid={`sa-nav-${item.path.split('/').pop() || 'overview'}`}
                      className={`group flex items-center gap-2.5 px-3 py-2 rounded-lg transition-all duration-150 ${
                        active ? 'bg-[#E8613A] text-white font-medium' : 'text-white/50 hover:bg-white/5 hover:text-white'
                      } ${collapsed ? 'justify-center px-0 mx-1' : ''}`}
                      onClick={() => setSidebarOpen(false)}
                      title={collapsed ? item.label : undefined}
                    >
                      <Icon size={18} strokeWidth={active ? 2.2 : 1.8} />
                      {!collapsed && <span className="text-[13px]">{item.label}</span>}
                    </Link>
                  );
                })}
              </div>
            </div>
          ))}
        </nav>

        <div className="border-t border-white/10 p-3 flex-shrink-0">
          <div className={`flex items-center ${collapsed ? 'justify-center' : 'justify-between'}`}>
            {!collapsed && (
              <div className="min-w-0">
                <p className="text-sm font-medium text-white truncate">{user?.name || user?.email}</p>
                <p className="text-[10px] text-white/40 uppercase tracking-wider">Super Admin</p>
              </div>
            )}
            <button onClick={handleLogout} className="p-2 text-white/40 hover:text-red-400 transition-colors" data-testid="sa-logout-btn">
              <LogOut size={18} />
            </button>
          </div>
        </div>
      </aside>

      <div className="flex-1 flex flex-col overflow-hidden min-w-0">
        <header className="h-14 bg-card border-b border-border px-6 flex items-center justify-between">
          <div className="flex items-center">
            <button onClick={() => setSidebarOpen(true)} className="lg:hidden text-muted-foreground hover:text-foreground mr-4">
              <Menu size={22} />
            </button>
            <h1 className="text-lg font-bold tracking-tight text-foreground" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
              {currentPage?.label || 'Super Admin'}
            </h1>
          </div>
          {user?.sales_code && (
            <Link
              to="/sales"
              className="hidden sm:inline-flex items-center gap-1.5 text-xs h-8 px-3 rounded-full bg-[#E8613A]/10 text-[#E8613A] font-semibold hover:bg-[#E8613A]/20 transition-colors"
              data-testid="sa-switch-to-sales"
            >
              <Sparkles size={11} /> Operio Sales ({user.sales_code})
            </Link>
          )}
        </header>
        <main className="flex-1 overflow-y-auto p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default SuperAdminLayout;
